import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { lumi } from '../lib/lumi'
import { useLeads } from '../hooks/useLeads'

export default function Admin() {
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loginForm, setLoginForm] = useState({ email: '', password: '' })
  const [registerForm, setRegisterForm] = useState({ email: '', password: '', userName: '', otp: '' })
  const [showRegister, setShowRegister] = useState(false)
  const [otpSent, setOtpSent] = useState(false)
  const [otpCooldown, setOtpCooldown] = useState(0)
  const [searchTerm, setSearchTerm] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [selectedLead, setSelectedLead] = useState<any>(null)

  const { leads, total, loading: leadsLoading, fetchLeads, searchLeads, filterByDateRange, updateLeadStatus, deleteLead } = useLeads()

  useEffect(() => {
    checkAuth()
  }, [])

  useEffect(() => {
    if (isLoggedIn) {
      fetchLeads()
    }
  }, [isLoggedIn])

  useEffect(() => {
    if (otpCooldown > 0) {
      const timer = setTimeout(() => setOtpCooldown(otpCooldown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [otpCooldown])

  const checkAuth = async () => {
    try {
      await lumi.auth.refreshUser()
      const user = lumi.auth.user
      
      if (user && user.userRole === 'ADMIN') {
        setIsLoggedIn(true)
      }
    } catch (error) {
      console.log('Usuário não autenticado')
    } finally {
      setLoading(false)
    }
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      const { user } = await lumi.auth.signInWithPassword({
        email: loginForm.email,
        password: loginForm.password
      })

      if (user.userRole === 'ADMIN') {
        setIsLoggedIn(true)
        alert('Login realizado com sucesso!')
      } else {
        alert('Acesso negado. Apenas administradores podem acessar esta área.')
        await lumi.auth.signOut()
      }
    } catch (error: any) {
      console.error('Erro no login:', error)
      alert('Erro no login: ' + (error.message || 'Verifique suas credenciais'))
    } finally {
      setLoading(false)
    }
  }

  const handleSendOtp = async () => {
    if (otpCooldown > 0) return

    try {
      await lumi.auth.sendOtp({
        email: registerForm.email,
        type: 'register'
      })
      
      setOtpSent(true)
      setOtpCooldown(60)
      alert('Código OTP enviado para seu e-mail!')
    } catch (error: any) {
      console.error('Erro ao enviar OTP:', error)
      alert('Erro ao enviar OTP: ' + (error.message || 'Tente novamente'))
    }
  }

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!otpSent) {
      alert('Você precisa solicitar o código OTP primeiro!')
      return
    }

    setLoading(true)

    try {
      await lumi.auth.signUpWithPassword({
        email: registerForm.email,
        password: registerForm.password,
        userName: registerForm.userName,
        otp: registerForm.otp
      })

      alert('Cadastro realizado com sucesso! Você será redirecionado para a área administrativa.')
      setIsLoggedIn(true)
      setShowRegister(false)
    } catch (error: any) {
      console.error('Erro no cadastro:', error)
      alert('Erro no cadastro: ' + (error.message || 'Verifique os dados'))
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = async () => {
    await lumi.auth.signOut()
    setIsLoggedIn(false)
    setLoginForm({ email: '', password: '' })
  }

  const handleSearch = () => {
    if (searchTerm.trim()) {
      searchLeads(searchTerm)
    } else {
      fetchLeads()
    }
  }

  const handleDateFilter = () => {
    if (startDate && endDate) {
      filterByDateRange(
        new Date(startDate).toISOString(),
        new Date(endDate + 'T23:59:59').toISOString()
      )
    } else {
      fetchLeads()
    }
  }

  const exportCSV = () => {
    const headers = ['Nome', 'Email', 'WhatsApp', 'Terra', 'Água', 'Fogo', 'Ar', 'Resultado', 'Protocolo Liberação', 'Leitura Vibracional', 'Data/Hora']
    const rows = leads.map(lead => [
      lead.nome,
      lead.email,
      lead.whatsapp,
      lead.terraScore,
      lead.aguaScore,
      lead.fogoScore,
      lead.arScore,
      lead.resultadoFinal,
      lead.interesseProtocoloLiberacao || 'Não respondido',
      lead.interesseLeituraVibracional || 'Não respondido',
      new Date(lead.createdAt).toLocaleString('pt-BR')
    ])

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n')

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `leads-4-elementos-${new Date().toISOString().split('T')[0]}.csv`
    link.click()
  }

  const getElementStatus = (score: number, maxScore: number) => {
    const percentage = maxScore > 0 ? (score / maxScore) * 100 : 0
    
    if (percentage >= 80) return 'Bloqueio Crítico'
    if (percentage >= 60) return 'Bloqueio Elevado'
    if (percentage >= 40) return 'Instabilidade Ativa'
    if (percentage >= 20) return 'Sensível'
    return 'Equilibrado'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-zinc-50 flex items-center justify-center">
        <div className="text-2xl text-gray-600">Carregando...</div>
      </div>
    )
  }

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-amber-50 to-purple-50 py-12 px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md mx-auto"
        >
          <div className="bg-white rounded-3xl shadow-2xl p-8">
            <h1 className="text-3xl font-bold text-center text-purple-900 mb-6">
              {showRegister ? 'Criar Conta Admin' : 'Área Administrativa'}
            </h1>
            
            {!showRegister ? (
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">E-mail</label>
                  <input
                    type="email"
                    required
                    value={loginForm.email}
                    onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:ring-2 focus:ring-purple-400"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">Senha</label>
                  <input
                    type="password"
                    required
                    value={loginForm.password}
                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:ring-2 focus:ring-purple-400"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-purple-600 to-purple-700 text-white font-bold py-3 rounded-xl hover:shadow-lg transition-all"
                >
                  Entrar
                </button>

                <button
                  type="button"
                  onClick={() => setShowRegister(true)}
                  className="w-full text-purple-600 hover:text-purple-800 font-medium"
                >
                  Criar primeira conta admin
                </button>
              </form>
            ) : (
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Nome</label>
                  <input
                    type="text"
                    required
                    value={registerForm.userName}
                    onChange={(e) => setRegisterForm({ ...registerForm, userName: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:ring-2 focus:ring-purple-400"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">E-mail</label>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      required
                      value={registerForm.email}
                      onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                      className="flex-1 px-4 py-3 border-2 border-purple-200 rounded-xl focus:ring-2 focus:ring-purple-400"
                    />
                    <button
                      type="button"
                      onClick={handleSendOtp}
                      disabled={otpCooldown > 0 || !registerForm.email}
                      className="px-4 py-3 bg-amber-500 text-white font-bold rounded-xl hover:bg-amber-600 disabled:opacity-50"
                    >
                      {otpCooldown > 0 ? `${otpCooldown}s` : 'Enviar OTP'}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">Código OTP</label>
                  <input
                    type="text"
                    required
                    value={registerForm.otp}
                    onChange={(e) => setRegisterForm({ ...registerForm, otp: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:ring-2 focus:ring-purple-400"
                    placeholder="Digite o código recebido"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">Senha</label>
                  <input
                    type="password"
                    required
                    value={registerForm.password}
                    onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:ring-2 focus:ring-purple-400"
                  />
                </div>

                <button
                  type="submit"
                  disabled={!otpSent}
                  className="w-full bg-gradient-to-r from-amber-500 to-yellow-500 text-white font-bold py-3 rounded-xl hover:shadow-lg transition-all disabled:opacity-50"
                >
                  Criar Conta Admin
                </button>

                <button
                  type="button"
                  onClick={() => setShowRegister(false)}
                  className="w-full text-purple-600 hover:text-purple-800 font-medium"
                >
                  Voltar para login
                </button>
              </form>
            )}
          </div>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-zinc-50 py-8 px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-[1600px] mx-auto"
      >
        <div className="bg-white rounded-3xl shadow-xl p-6 md:p-8 mb-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 mb-2">
                Painel Administrativo - Teste dos 4 Elementos
              </h1>
              <p className="text-gray-600">
                Gerenciamento de leads • {total} cadastrados
              </p>
            </div>
            <div className="flex gap-2">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={exportCSV}
                className="bg-gradient-to-r from-green-600 to-emerald-600 text-white font-bold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all"
              >
                📊 Exportar CSV
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleLogout}
                className="bg-gradient-to-r from-red-600 to-rose-600 text-white font-bold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all"
              >
                Sair
              </motion.button>
            </div>
          </div>

          {/* Filtros */}
          <div className="bg-gray-50 rounded-2xl p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Buscar por nome, email ou WhatsApp
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Digite para buscar..."
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400"
                  />
                  <button
                    onClick={handleSearch}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                  >
                    🔍
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Data Inicial
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Data Final
                </label>
                <div className="flex gap-2">
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400"
                  />
                  <button
                    onClick={handleDateFilter}
                    className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
                  >
                    Filtrar
                  </button>
                </div>
              </div>
            </div>

            {(searchTerm || startDate || endDate) && (
              <button
                onClick={() => {
                  setSearchTerm('')
                  setStartDate('')
                  setEndDate('')
                  fetchLeads()
                }}
                className="mt-4 text-purple-600 hover:text-purple-800 font-medium"
              >
                Limpar filtros
              </button>
            )}
          </div>

          {/* Estatísticas */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-4">
              <p className="text-blue-600 text-sm font-medium mb-1">Total de Leads</p>
              <p className="text-3xl font-bold text-blue-700">{total}</p>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-4">
              <p className="text-green-600 text-sm font-medium mb-1">🌱 Terra</p>
              <p className="text-3xl font-bold text-green-700">
                {leads.filter(l => l.resultadoFinal === 'terra').length}
              </p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-cyan-100 rounded-2xl p-4">
              <p className="text-cyan-600 text-sm font-medium mb-1">🌊 Água</p>
              <p className="text-3xl font-bold text-cyan-700">
                {leads.filter(l => l.resultadoFinal === 'agua').length}
              </p>
            </div>
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl p-4">
              <p className="text-orange-600 text-sm font-medium mb-1">🔥 Fogo</p>
              <p className="text-3xl font-bold text-orange-700">
                {leads.filter(l => l.resultadoFinal === 'fogo').length}
              </p>
            </div>
          </div>
        </div>

        {/* Tabela de Leads */}
        <div className="bg-white rounded-3xl shadow-xl">
          {leadsLoading ? (
            <div className="p-12 text-center text-gray-600">
              Carregando leads...
            </div>
          ) : leads.length === 0 ? (
            <div className="p-12 text-center text-gray-600">
              Nenhum lead encontrado
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-[1280px] w-full text-xs">
                <thead className="bg-gray-50 border-b-2 border-gray-200">
                  <tr>
                    <th className="px-3 py-3 text-left text-[11px] font-semibold text-gray-700 uppercase whitespace-nowrap">
                      Nome
                    </th>
                    <th className="px-3 py-3 text-left text-[11px] font-semibold text-gray-700 uppercase whitespace-nowrap">
                      Contato
                    </th>
                    <th className="px-3 py-3 text-center text-[11px] font-semibold text-gray-700 uppercase whitespace-nowrap">
                      Resultado
                    </th>
                    <th className="px-3 py-3 text-center text-[11px] font-semibold text-gray-700 uppercase whitespace-nowrap">
                      P13 Protocolo
                    </th>
                    <th className="px-3 py-3 text-center text-[11px] font-semibold text-gray-700 uppercase whitespace-nowrap">
                      P14 Leitura
                    </th>
                    <th className="px-3 py-3 text-center text-[11px] font-semibold text-gray-700 uppercase whitespace-nowrap">
                      Status
                    </th>
                    <th className="px-3 py-3 text-center text-[11px] font-semibold text-gray-700 uppercase whitespace-nowrap">
                      Data/Hora
                    </th>
                    <th className="px-3 py-3 text-center text-[11px] font-semibold text-gray-700 uppercase whitespace-nowrap">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {leads.map((lead) => {
                    return (
                      <tr key={lead._id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-3 py-3 whitespace-nowrap">
                          <div className="text-xs font-medium text-gray-900">{lead.nome}</div>
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          <div className="text-xs text-gray-600">{lead.email}</div>
                          <div className="text-[11px] text-gray-500">{lead.whatsapp}</div>
                        </td>
                        <td className="px-3 py-3 text-center whitespace-nowrap">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-[11px] font-medium bg-purple-100 text-purple-800 capitalize">
                            {lead.resultadoFinal === 'terra' && '🌱 Terra'}
                            {lead.resultadoFinal === 'agua' && '🌊 Água'}
                            {lead.resultadoFinal === 'fogo' && '🔥 Fogo'}
                            {lead.resultadoFinal === 'ar' && '🌪️ Ar'}
                            {lead.resultadoFinal === 'nenhum_bloqueio' && '🌟 Equilibrado'}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-center whitespace-nowrap">
                          <span className={`px-2 py-1 text-[11px] font-semibold rounded-full ${
                            lead.interesseProtocoloLiberacao === 'Sim, quero saber mais'
                              ? 'bg-green-100 text-green-800'
                              : lead.interesseProtocoloLiberacao === 'Não, por enquanto não'
                              ? 'bg-gray-100 text-gray-600'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {lead.interesseProtocoloLiberacao === 'Sim, quero saber mais' ? '✓ Sim' :
                             lead.interesseProtocoloLiberacao === 'Não, por enquanto não' ? '✗ Não' :
                             '⚠ Não respondido'}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-center whitespace-nowrap">
                          <span className={`px-2 py-1 text-[11px] font-semibold rounded-full ${
                            lead.interesseLeituraVibracional === 'Sim, quero saber mais'
                              ? 'bg-green-100 text-green-800'
                              : lead.interesseLeituraVibracional === 'Não, por enquanto não'
                              ? 'bg-gray-100 text-gray-600'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {lead.interesseLeituraVibracional === 'Sim, quero saber mais' ? '✓ Sim' : 
                             lead.interesseLeituraVibracional === 'Não, por enquanto não' ? '✗ Não' :
                             '⚠ Não respondido'}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-center whitespace-nowrap">
                          <select
                            value={lead.status || 'Novo'}
                            onChange={(e) => updateLeadStatus(lead._id, e.target.value)}
                            className={`px-2 py-1 text-xs font-semibold border-2 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 cursor-pointer ${
                              lead.status === 'Novo' ? 'bg-blue-100 text-blue-800 border-blue-300' :
                              lead.status === 'Contatado' ? 'bg-amber-100 text-amber-800 border-amber-300' :
                              lead.status === 'Agendado' ? 'bg-purple-100 text-purple-800 border-purple-300' :
                              lead.status === 'Não respondeu' ? 'bg-gray-100 text-gray-800 border-gray-300' :
                              lead.status === 'Não qualificado' ? 'bg-red-100 text-red-800 border-red-300' :
                              lead.status === 'Convertido' ? 'bg-green-100 text-green-800 border-green-300' :
                              'bg-blue-100 text-blue-800 border-blue-300'
                            }`}
                          >
                            <option value="Novo">Novo</option>
                            <option value="Contatado">Contatado</option>
                            <option value="Agendado">Agendado</option>
                            <option value="Não respondeu">Não respondeu</option>
                            <option value="Não qualificado">Não qualificado</option>
                            <option value="Convertido">✓ Convertido</option>
                          </select>
                        </td>
                        <td className="px-3 py-3 text-center whitespace-nowrap">
                          <span className="text-xs text-gray-600">
                            {new Date(lead.createdAt).toLocaleString('pt-BR')}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-center whitespace-nowrap">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => setSelectedLead(lead)}
                              className="text-purple-600 hover:text-purple-800 font-medium text-xs"
                            >
                              Ver Detalhes
                            </button>
                            <button
                              onClick={() => {
                                if (window.confirm('Tem certeza que deseja deletar este lead?')) {
                                  deleteLead(lead._id)
                                }
                              }}
                              className="text-red-600 hover:text-red-800 font-medium text-xs"
                            >
                              🗑️
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Modal de Detalhes */}
        {selectedLead && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-3xl shadow-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="flex justify-between items-start mb-6">
                <h2 className="text-2xl font-bold text-gray-800">
                  Detalhes do Lead
                </h2>
                <button
                  onClick={() => setSelectedLead(null)}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-700 mb-3">Informações Pessoais</h3>
                  <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                    <p><span className="font-medium">Nome:</span> {selectedLead.nome}</p>
                    <p><span className="font-medium">Email:</span> {selectedLead.email}</p>
                    <p><span className="font-medium">WhatsApp:</span> {selectedLead.whatsapp}</p>
                    <p><span className="font-medium">Data:</span> {new Date(selectedLead.createdAt).toLocaleString('pt-BR')}</p>
                    <p className="pt-2 border-t border-gray-200"><span className="font-medium">Interesse no Protocolo de Liberação:</span> <span className={`font-semibold ${
                      selectedLead.interesseProtocoloLiberacao === 'Sim, quero saber mais' ? 'text-green-600' :
                      selectedLead.interesseProtocoloLiberacao === 'Não, por enquanto não' ? 'text-gray-600' :
                      'text-yellow-600'
                    }`}>{selectedLead.interesseProtocoloLiberacao || 'Não respondido'}</span></p>
                    <p className="pt-2 border-t border-gray-200"><span className="font-medium">Interesse em Leitura Vibracional:</span> <span className={`font-semibold ${
                      selectedLead.interesseLeituraVibracional === 'Sim, quero saber mais' ? 'text-green-600' : 
                      selectedLead.interesseLeituraVibracional === 'Não, por enquanto não' ? 'text-gray-600' :
                      'text-yellow-600'
                    }`}>{selectedLead.interesseLeituraVibracional || 'Não respondido'}</span></p>
                    <div className="pt-2 border-t border-gray-200">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Status do Lead:</label>
                      <select
                        value={selectedLead.status || 'Novo'}
                        onChange={(e) => {
                          updateLeadStatus(selectedLead._id, e.target.value)
                          setSelectedLead({ ...selectedLead, status: e.target.value })
                        }}
                        className={`w-full px-4 py-2.5 border-2 rounded-lg focus:ring-2 focus:ring-purple-400 font-semibold text-base cursor-pointer ${
                          selectedLead.status === 'Novo' ? 'bg-blue-100 text-blue-800 border-blue-300' :
                          selectedLead.status === 'Contatado' ? 'bg-amber-100 text-amber-800 border-amber-300' :
                          selectedLead.status === 'Agendado' ? 'bg-purple-100 text-purple-800 border-purple-300' :
                          selectedLead.status === 'Não respondeu' ? 'bg-gray-100 text-gray-800 border-gray-300' :
                          selectedLead.status === 'Não qualificado' ? 'bg-red-100 text-red-800 border-red-300' :
                          selectedLead.status === 'Convertido' ? 'bg-green-100 text-green-800 border-green-300' :
                          'bg-blue-100 text-blue-800 border-blue-300'
                        }`}
                      >
                        <option value="Novo">Novo</option>
                        <option value="Contatado">Contatado</option>
                        <option value="Agendado">Agendado</option>
                        <option value="Não respondeu">Não respondeu</option>
                        <option value="Não qualificado">Não qualificado</option>
                        <option value="Convertido">✓ Convertido</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-700 mb-3">
                    🧩 Visão dos 4 Elementos
                  </h3>
                  <div className="grid grid-cols-1 gap-3">
                    {(() => {
                      const maxScore = Math.max(
                        selectedLead.terraScore,
                        selectedLead.aguaScore,
                        selectedLead.fogoScore,
                        selectedLead.arScore
                      )
                      
                      return (
                        <>
                          <div className="bg-green-50 rounded-xl p-4 border-2 border-green-200">
                            <div className="flex justify-between items-center">
                              <span className="font-medium text-green-800">🌱 Terra</span>
                              <span className="text-sm font-bold text-green-700">
                                {selectedLead.terraScore}/3 • {((selectedLead.terraScore / 3) * 100).toFixed(0)}% • {getElementStatus(selectedLead.terraScore, 3)}
                              </span>
                            </div>
                          </div>

                          <div className="bg-blue-50 rounded-xl p-4 border-2 border-blue-200">
                            <div className="flex justify-between items-center">
                              <span className="font-medium text-blue-800">🌊 Água</span>
                              <span className="text-sm font-bold text-blue-700">
                                {selectedLead.aguaScore}/3 • {((selectedLead.aguaScore / 3) * 100).toFixed(0)}% • {getElementStatus(selectedLead.aguaScore, 3)}
                              </span>
                            </div>
                          </div>

                          <div className="bg-orange-50 rounded-xl p-4 border-2 border-orange-200">
                            <div className="flex justify-between items-center">
                              <span className="font-medium text-orange-800">🔥 Fogo</span>
                              <span className="text-sm font-bold text-orange-700">
                                {selectedLead.fogoScore}/3 • {((selectedLead.fogoScore / 3) * 100).toFixed(0)}% • {getElementStatus(selectedLead.fogoScore, 3)}
                              </span>
                            </div>
                          </div>

                          <div className="bg-purple-50 rounded-xl p-4 border-2 border-purple-200">
                            <div className="flex justify-between items-center">
                              <span className="font-medium text-purple-800">🌪️ Ar</span>
                              <span className="text-sm font-bold text-purple-700">
                                {selectedLead.arScore}/3 • {((selectedLead.arScore / 3) * 100).toFixed(0)}% • {getElementStatus(selectedLead.arScore, 3)}
                              </span>
                            </div>
                          </div>
                        </>
                      )
                    })()}
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-700 mb-3">Resultado Final</h3>
                  <div className="bg-purple-50 rounded-xl p-4 border-2 border-purple-200">
                    <p className="text-center text-xl font-bold text-purple-800 capitalize">
                      {selectedLead.resultadoFinal === 'terra' && '🌱 Elemento Terra'}
                      {selectedLead.resultadoFinal === 'agua' && '🌊 Elemento Água'}
                      {selectedLead.resultadoFinal === 'fogo' && '🔥 Elemento Fogo'}
                      {selectedLead.resultadoFinal === 'ar' && '🌪️ Elemento Ar'}
                      {selectedLead.resultadoFinal === 'nenhum_bloqueio' && '🌟 Nenhum Bloqueio Identificado'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => {
                    if (window.confirm('Tem certeza que deseja deletar este lead?')) {
                      deleteLead(selectedLead._id)
                      setSelectedLead(null)
                    }
                  }}
                  className="flex-1 bg-red-600 text-white font-bold py-3 rounded-xl hover:bg-red-700 transition-colors"
                >
                  🗑️ Deletar Lead
                </button>
                <button
                  onClick={() => setSelectedLead(null)}
                  className="flex-1 bg-purple-600 text-white font-bold py-3 rounded-xl hover:bg-purple-700 transition-colors"
                >
                  Fechar
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </motion.div>
    </div>
  )
}
